// src/pages/pessoas/ClienteCad.js
import React, { useState, useEffect } from 'react';

import { FaCoins } from "react-icons/fa";
import Input from '../../components/input/Input';
import { Cliente } from '../../models/pessoas/Cliente.js';
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import InputCheck from '../../components/input/InputCheck.js';
import { formatDate } from '../../utils/dateTimeUtils.js';
import { formatCurrency } from '../../utils/numberUtils.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import EnderecoForm from '../../components/form/pessoa/EnderecoForm.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import ModalFooter from '../../components/modalForm/ModalFooter.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import { useFilial } from '../../contexts/administrativo/FilialContext.js';
import { getCashBackSaldoPorPessoa } from '../../services/pedidos/CashBackService.js';
import ModalCashBackMov from './ModalCashBackMov.js';
import ModalTabs from '../../components/modalForm/ModalTabs';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const ClienteCad = ({ show, onHide, initialCliente, onSaveCliente, novoLimpa=true }) => {
    const { clientes, addCliente, updateCliente, deleteCliente, showClienteSearch } = useCliente();
    const { filialLogin } = useFilial();    
    const [cliente, setCliente] = useState(new Cliente());
    const [novoReg, setNovoReg] = useState(true);
    const [activeTab, setActiveTab] = useState('geral');
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();
    const [tipoPessoa, setTipoPessoa] = useState('F');
    const [isSaving, setIsSaving] = useState(false);
    const [cashBackSaldo, setCashBackSaldo] = useState(null);
    const [showModalCashBackMov, setShowModalCashBackMov] = useState(false);
    const exibeAbaConfig = filialLogin?.cnpj === '61.832.329/0001-03';
    //const exibeAbaConfig = true;
    const pessoaId = cliente?.pessoaId || cliente?.pessoa?.id;
    const validadeCashBack = cashBackSaldo?.validade ? new Date(cashBackSaldo.validade) : null;
    const cashBackDisponivel = (cashBackSaldo?.saldo || 0) > 0 &&
        (!validadeCashBack || validadeCashBack >= new Date(new Date().toDateString()));

    const handleBuscarCliente = async () => {
        const clienteSel = await showClienteSearch();
        console.log('Cliente selecionado:', clienteSel);

        if (clienteSel) {
            setCliente(clienteSel);
            setTipoPessoa(clienteSel.pessoa?.fisicaId ? 'F' : 'J');
            handleEditar(clienteSel.id);
        } else {
            setCliente(new Cliente());
            setTipoPessoa('F');
        }
    };

    const handleEditar = (id) => {
        if (id === 0 || !id) {
            showMessage("Selecione um cliente para editar!");
            return;
        }
        setNovoReg(false);
    };

    const handleExit = () => {
        // O foco pode voltar ao código ao fechar a busca de cidade.
        // Sem código informado, preserva os dados do novo cliente.
        if (!cliente?.id) return;

        const clienteSelecionado = clientes.find(f => f.id.toString() === cliente?.id?.toString());
        if (clienteSelecionado) {
            setCliente(clienteSelecionado);
            setTipoPessoa(clienteSelecionado.pessoa?.fisicaId ? 'F' : 'J');
            handleEditar(clienteSelecionado?.id);
        } else {
            setCliente(new Cliente());
            setTipoPessoa('F');
        }
    };

    // Ao abrir o modal, carrega cliente inicial (quando informado) ou inicia novo
    useEffect(() => {
        if (show) {
            if (initialCliente && initialCliente.id) {
                const clienteInicial = JSON.parse(JSON.stringify(initialCliente));
                setCliente(clienteInicial);
                setTipoPessoa(clienteInicial.pessoa?.fisicaId ? 'F' : 'J');
                setNovoReg(false);
                setIsSaving(false);

            } else {
                handleNovo();
            }

            console.log('login:', filialLogin);
        }
    }, [show, initialCliente]);

    useEffect(() => {
        if (!exibeAbaConfig && activeTab === 'config') {
            setActiveTab('geral');
        }
    }, [exibeAbaConfig, activeTab]);

    useEffect(() => {
        const carregarCashBack = async () => {
            if (!show || !pessoaId) {
                setCashBackSaldo(null);
                return;
            }

            try {
                const result = await getCashBackSaldoPorPessoa(pessoaId);
                setCashBackSaldo(result);
            } catch (error) {
                console.error('Erro ao buscar saldo de cashback:', error);
                setCashBackSaldo(null);
            }
        };

        carregarCashBack();
    }, [show, pessoaId]);

    const handleNovo = () => {
        setCliente(new Cliente());
        setNovoReg(true);
        setTipoPessoa('F');
        setIsSaving(false);
        setCashBackSaldo(null);
        setShowModalCashBackMov(false);
    };

    const handleDelete = async () => {
        if (!cliente.id) {
            showMessage("Selecione um cliente para excluir!");
        } else {
            const resposta = await showYesNo("Deseja excluir o cliente selecionado?");
            if (resposta) {
                await deleteCliente(cliente.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        if (isSaving) return; // Previne cliques múltiplos

        setIsSaving(true);
        try {
            if (!cliente.pessoa?.nome) {
                showMessage("Nome não preenchido!", 'warning');
                return;
            }

            const endereco = cliente.pessoa?.endereco;
            const logradouroInformado = endereco?.logradouro?.trim();

            if (logradouroInformado && !endereco?.bairro?.trim()) {
                setActiveTab('endereco');
                showMessage("Informe o bairro do cliente!", 'warning');
                return;
            }

            if (logradouroInformado && !endereco?.numero?.trim()) {
                setActiveTab('endereco');
                showMessage("Informe o número do cliente ou S/N", 'warning');
                return;
            }

            const clientePayload = { ...cliente };

            if (tipoPessoa === 'J') {
                delete clientePayload.pessoa.fisica;
                //delete clientePayload.pessoa.fisicaId;
            } else {
                delete clientePayload.pessoa.juridica;
                //delete clientePayload.pessoa.juridicaId;
            }

            const clienteSalvo = !clientePayload.id
                ? await addCliente(clientePayload)
                : await updateCliente(clientePayload);

            const clienteAtual = clienteSalvo || clientePayload;
            if (novoLimpa) {
                handleNovo();
            } else {
                setCliente(clienteAtual);
                setNovoReg(false);
            }
            if (onSaveCliente) {
                onSaveCliente(clienteAtual);
            }
        } catch (error) {
            showMessage(error.message || String(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleTabChange = (tab) => (e) => {
        e.preventDefault();
        setActiveTab(tab);
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura='650px' zIndex={1060} variant="cadastro" onHide={onHide}>
            <ModalHeader title={novoReg ? 'Novo Cliente' : 'Alterar Cliente'} onHide={onHide} />
            <ModalBody>
                <form>
                    <div className="input-group">
                        <Input
                            label="Código"
                            value={cliente.id || ''}
                            onChange={(e) => setCliente({ ...cliente, id: e.target.value })}
                            disabled={!novoReg}
                            btnBuscar={handleBuscarCliente}
                            onBlur={handleExit}
                        />
                        <Input
                            label="Nome"
                            value={cliente.pessoa?.nome || ''}
                            onChange={(e) => setCliente({ ...cliente, pessoa: { ...cliente?.pessoa, nome: e.target.value } })}
                            size={300}
                        />
                        <Input
                            label="Data Cad."
                            value={cliente.dataCad ? formatDate(cliente.dataCad) : ''}
                            disabled
                            size={100}
                        />
                    </div>

                    <ModalTabs  style={{ marginTop: '10px' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={handleTabChange('geral')}
                            >
                                Geral
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'contato' ? 'active' : ''}`}
                                onClick={handleTabChange('contato')}
                            >
                                Contatos
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'endereco' ? 'active' : ''}`}
                                onClick={handleTabChange('endereco')}
                            >
                                Endereço
                            </button>
                        </li>
                        {exibeAbaConfig && (
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'config' ? 'active' : ''}`}
                                    onClick={handleTabChange('config')}
                                >
                                    Config
                                </button>
                            </li>
                        )}
                    </ModalTabs>

                    <ModalTabs.Panel >
                        {activeTab === 'geral' && (
                            <div className="section">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                    <h5>Geral</h5>
                                    {cashBackDisponivel && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>
                                                Cashback: <strong>{formatCurrency(cashBackSaldo.saldo)}</strong>
                                                {cashBackSaldo.validade && (
                                                    <> até {formatDate(cashBackSaldo.validade)}</>
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm"
                                                title="Ver movimentações de cashback"
                                                onClick={() => setShowModalCashBackMov(true)}
                                                style={{ paddingTop: '2px', paddingBottom: '2px' }}
                                            >
                                                <FaCoins />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="input-group">
                                    <InputCheck
                                        label="Pessoa Física"
                                        checked={tipoPessoa === 'F'}
                                        onChange={() => setTipoPessoa('F')}
                                    />
                                    <InputCheck
                                        label="Pessoa Jurídica"
                                        checked={tipoPessoa === 'J'}
                                        onChange={() => setTipoPessoa('J')}
                                    />
                                </div>

                                {tipoPessoa === 'F' && (
                                    <div>
                                        <div className="input-group">
                                            <Input
                                                label="CPF"
                                                value={cliente.pessoa?.fisica?.cpf || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        fisica: {
                                                            ...cliente.pessoa?.fisica,
                                                            cpf: e.target.value
                                                        }
                                                    }
                                                })}
                                                mask="999.999.999-99"
                                                size={150}
                                            />
                                            <Input
                                                label="RG"
                                                value={cliente.pessoa?.fisica?.rg || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        fisica: {
                                                            ...cliente.pessoa?.fisica,
                                                            rg: e.target.value
                                                        }
                                                    }
                                                })}
                                                size={150}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <Input
                                                label="Data Nasc."
                                                value={cliente.pessoa?.fisica?.dataNascimento || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        fisica: {
                                                            ...cliente.pessoa?.fisica,
                                                            dataNascimento: e.target.value
                                                        }
                                                    }
                                                })}
                                                type="date"
                                                size={150}
                                            />
                                            <Input
                                                label="Profissão"
                                                value={cliente.pessoa?.fisica?.profissao || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        fisica: {
                                                            ...cliente.pessoa?.fisica,
                                                            profissao: e.target.value
                                                        }
                                                    }
                                                })}
                                                size={300}
                                            />
                                        </div>
                                    </div>
                                )}
                                {tipoPessoa === 'J' && (
                                    <>
                                        <div className="input-group">
                                            <Input
                                                label="CNPJ"
                                                value={cliente.pessoa?.juridica?.cnpj || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        juridica: {
                                                            ...cliente.pessoa?.juridica,
                                                            cnpj: e.target.value
                                                        }
                                                    }
                                                })}
                                                mask="99.999.999/9999-99"
                                                size={170}
                                            />
                                            <Input
                                                label="Razão Social"
                                                value={cliente.pessoa?.juridica?.razaoSocial || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        juridica: {
                                                            ...cliente.pessoa?.juridica,
                                                            razaoSocial: e.target.value
                                                        }
                                                    }
                                                })}
                                                size={300}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <Input
                                                label="Inscrição Estadual"
                                                value={cliente.pessoa?.juridica?.ie || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        juridica: {
                                                            ...cliente.pessoa?.juridica,
                                                            ie: e.target.value
                                                        }
                                                    }
                                                })}
                                                size={150}
                                            />
                                            <Input
                                                label="Inscrição Municipal"
                                                value={cliente.pessoa?.juridica?.im || ''}
                                                onChange={(e) => setCliente({
                                                    ...cliente,
                                                    pessoa: {
                                                        ...cliente.pessoa,
                                                        juridica: {
                                                            ...cliente.pessoa?.juridica,
                                                            im: e.target.value
                                                        }
                                                    }
                                                })}
                                                size={150}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {activeTab === 'contato' && (
                            <div className="section">
                                <h5>Contato</h5>
                                <div className="input-group">
                                    <Input
                                        label="Telefone"
                                        value={cliente.pessoa?.telefone?.numero || ''}
                                        onChange={(e) => setCliente({
                                            ...cliente,
                                            pessoa: {
                                                ...cliente.pessoa,
                                                telefone: {
                                                    ...cliente.pessoa?.telefone,
                                                    numero: e.target.value
                                                }
                                            }
                                        })}
                                        size={150}
                                        mask="(99) 9 9999-9999"
                                    />
                                    <Input
                                        label="E-mail"
                                        value={cliente.pessoa?.email?.enderecoEmail || ''}
                                        onChange={(e) => setCliente({
                                            ...cliente,
                                            pessoa: {
                                                ...cliente.pessoa,
                                                email: {
                                                    ...cliente.pessoa?.email,
                                                    enderecoEmail: e.target.value
                                                }
                                            }
                                        })}
                                        size={300}
                                    />
                                </div>
                            </div>
                        )}
                        {activeTab === 'endereco' && (
                            <div>
                                <EnderecoForm
                                    endereco={cliente.pessoa?.endereco}
                                    setEndereco={(newEndereco) =>
                                        setCliente({
                                            ...cliente,
                                            pessoa: {
                                                ...cliente.pessoa,
                                                endereco: newEndereco
                                            }
                                        })
                                    }
                                />
                            </div>
                        )}
                        {exibeAbaConfig && activeTab === 'config' && (
                            <div className="section">
                                <h5>Config</h5>
                                <div className="input-group">
                                    <Input
                                        label="UrlApi"
                                        value={cliente.urlApi || ''}
                                        onChange={(e) => setCliente({ ...cliente, urlApi: e.target.value })}
                                        size={500}
                                    />
                                </div>
                            </div>
                        )}
                    </ModalTabs.Panel>
                </form>
            </ModalBody>
            <ModalFooter>
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} disabled={isSaving} style={{ paddingTop: '5px', paddingBottom: '5px' }} action="save">
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg || isSaving} style={{ paddingTop: '5px', paddingBottom: '5px' }} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg || isSaving} style={{ paddingTop: '5px', paddingBottom: '5px' }} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>
            <ModalCashBackMov
                show={showModalCashBackMov}
                onHide={() => setShowModalCashBackMov(false)}
                cliente={cliente}
            />
        </ModalContainer>
    );
};

export default ClienteCad;

